import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, SportsSettings, SportsLeague, SportsTeam, SportsGenericChannel, SportsPanelSource, StadiumCategoryConfig, UpdateStadiumCategoryRequest, CreatePanelSourceRequest, UpdatePanelSourceRequest } from '@/lib/api'
import {
  Trophy, Settings, Users, Calendar, RefreshCw, Check, X, AlertCircle,
  Play, Pause, Server, Tv, Link2, Search, Hash, Trash2, Filter, Plus, Database, Star, Edit2
} from 'lucide-react'
import { SearchableStreamSelect } from '@/components/SearchableStreamSelect'

type Tab = 'status' | 'settings' | 'sources' | 'leagues' | 'stadium' | 'teams' | 'channels'

export default function SportsEPG() {
  const [activeTab, setActiveTab] = useState<Tab>('status')
  const [selectedLeague, setSelectedLeague] = useState<string | null>(null)
  const [testingPanel, setTestingPanel] = useState(false)
  const [testingEmby, setTestingEmby] = useState(false)
  const [panelTestResult, setPanelTestResult] = useState<{ success: boolean; message?: string } | null>(null)
  const [embyTestResult, setEmbyTestResult] = useState<{ success: boolean; message?: string } | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all')
  const [editingSource, setEditingSource] = useState<SportsPanelSource | null>(null)
  const [showSourceModal, setShowSourceModal] = useState(false)
  const [sourceFormData, setSourceFormData] = useState<CreatePanelSourceRequest>({
    name: '',
    url: '',
    username: '',
    password: '',
    category_filter: '',
    is_default: false,
  })
  const [testingSourceId, setTestingSourceId] = useState<string | null>(null)
  const [sourceTestResults, setSourceTestResults] = useState<Record<string, { success: boolean; message?: string }>>({})
  const queryClient = useQueryClient()

  // Settings state
  const [settings, setSettings] = useState<SportsSettings>({
    emby_enabled: false,
    emby_url: '',
    emby_api_key: '',
    panel_url: '',
    panel_username: '',
    panel_password: '',
    epg_output_dir: '/data/epg',
    timezone: 'America/New_York',
  })

  // Fetch data
  const { data: sportsSettings } = useQuery({
    queryKey: ['sports-settings'],
    queryFn: () => api.getSportsSettings(),
  })

  const { data: sportsStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['sports-status'],
    queryFn: () => api.getSportsStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const { data: leagues } = useQuery({
    queryKey: ['sports-leagues'],
    queryFn: () => api.getSportsLeagues(),
  })

  const {
    data: stadiumCategories,
    isLoading: stadiumCategoriesLoading,
    isError: stadiumCategoriesError,
    refetch: refetchStadiumCategories,
  } = useQuery({
    queryKey: ['stadium-categories'],
    queryFn: () => api.getStadiumCategories(),
  })

  const { data: teams } = useQuery({
    queryKey: ['sports-teams', selectedLeague],
    queryFn: () => selectedLeague ? api.getSportsTeams(selectedLeague) : Promise.resolve([]),
    enabled: !!selectedLeague,
  })

  const { data: panelSources, refetch: refetchSources } = useQuery({
    queryKey: ['panel-sources'],
    queryFn: () => api.getPanelSources(),
  })

  const { data: streams, isLoading: streamsLoading } = useQuery({
    queryKey: ['streams-for-mapping'],
    queryFn: () => api.getStreams({ per_page: 10000 }),
  })

  // Fetch categories for filtering streams
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.getCategories(),
  })

  // Filter streams by selected category
  const filteredStreams = useMemo(() => {
    if (!streams?.streams) return []
    if (selectedCategoryFilter === 'all') return streams.streams
    if (selectedCategoryFilter === 'none') {
      return streams.streams.filter((s: { category_id?: string }) => !s.category_id)
    }
    return streams.streams.filter((s: { category_id?: string }) => s.category_id === selectedCategoryFilter)
  }, [streams?.streams, selectedCategoryFilter])

  // Calculate stream counts per category from streams data
  const categoryStreamCounts = useMemo(() => {
    if (!streams?.streams) return {}
    const counts: Record<string, number> = {}
    streams.streams.forEach((s: { category_id?: string }) => {
      if (s.category_id) {
        counts[s.category_id] = (counts[s.category_id] || 0) + 1
      }
    })
    return counts
  }, [streams?.streams, categories])

  // Get NFL/generic channel leagues and their channels
  const genericChannelLeagues = leagues?.filter((l: SportsLeague) => l.use_generic_channels && l.enabled) || []
  const selectedGenericLeague = genericChannelLeagues.find((l: SportsLeague) => l.code === selectedLeague)

  const { data: genericChannels, refetch: refetchChannels } = useQuery({
    queryKey: ['sports-generic-channels', selectedLeague],
    queryFn: () => selectedLeague ? api.getSportsGenericChannels(selectedLeague) : Promise.resolve([]),
    enabled: !!selectedLeague && !!selectedGenericLeague,
  })

  // Update local state when settings load
  useEffect(() => {
    if (sportsSettings) {
      setSettings({
        emby_enabled: sportsSettings.emby_enabled ?? false,
        emby_url: sportsSettings.emby_url ?? '',
        emby_api_key: sportsSettings.emby_api_key ?? '',
        panel_url: sportsSettings.panel_url ?? '',
        panel_username: sportsSettings.panel_username ?? '',
        panel_password: sportsSettings.panel_password ?? '',
        epg_output_dir: sportsSettings.epg_output_dir ?? '/data/epg',
        timezone: sportsSettings.timezone ?? 'America/New_York',
      })
    }
  }, [sportsSettings])

  // Mutations
  const updateSettingsMutation = useMutation({
    mutationFn: (newSettings: Partial<SportsSettings>) => api.updateSportsSettings(newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sports-settings'] })
    },
  })

  const updateStadiumMutation = useMutation({
    mutationFn: ({ code, update }: { code: string; update: UpdateStadiumCategoryRequest }) =>
      api.updateStadiumCategory(code, update),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stadium-categories'] }),
  })

  const updateLeagueMutation = useMutation({
    mutationFn: ({ code, update }: { code: string; update: { enabled?: boolean; panel_category?: string; panel_source_id?: string | null; use_generic_channels?: boolean; generic_channel_count?: number } }) =>
      api.updateSportsLeague(code, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sports-leagues'] })
      queryClient.invalidateQueries({ queryKey: ['sports-generic-channels'] })
    },
  })

  const createMappingMutation = useMutation({
    mutationFn: ({ teamId, streamId }: { teamId: string; streamId: string }) =>
      api.createSportsChannelMapping({ team_id: teamId, stream_id: streamId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sports-teams'] })
    },
  })

  const updateMappingMutation = useMutation({
    mutationFn: ({ mappingId, streamId }: { mappingId: string; streamId: string }) =>
      api.updateSportsChannelMapping(mappingId, { stream_id: streamId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sports-teams'] })
    },
  })

  const deleteMappingMutation = useMutation({
    mutationFn: (mappingId: string) => api.deleteSportsChannelMapping(mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sports-teams'] })
    },
  })

  const refreshMutation = useMutation({
    mutationFn: (leagueCode?: string) => api.triggerSportsRefresh(leagueCode),
    onSuccess: () => {
      refetchStatus()
    },
  })

  const mapGenericChannelMutation = useMutation({
    mutationFn: ({ channelId, streamId }: { channelId: string; streamId: string | null }) =>
      streamId
        ? api.mapSportsGenericChannelStream(channelId, { stream_id: streamId })
        : api.updateSportsGenericChannel(channelId, { stream_id: '' }), // Clear mapping
    onSuccess: () => {
      refetchChannels()
    },
  })

  const updateGenericChannelMutation = useMutation({
    mutationFn: ({ channelId, enabled }: { channelId: string; enabled: boolean }) =>
      api.updateSportsGenericChannel(channelId, { enabled }),
    onSuccess: () => {
      refetchChannels()
    },
  })

  // Panel Source mutations
  const createSourceMutation = useMutation({
    mutationFn: (source: CreatePanelSourceRequest) => api.createPanelSource(source),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['panel-sources'] })
      setShowSourceModal(false)
      resetSourceForm()
    },
  })

  const updateSourceMutation = useMutation({
    mutationFn: ({ id, source }: { id: string; source: UpdatePanelSourceRequest }) =>
      api.updatePanelSource(id, source),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['panel-sources'] })
      setShowSourceModal(false)
      setEditingSource(null)
      resetSourceForm()
    },
  })

  const deleteSourceMutation = useMutation({
    mutationFn: (id: string) => api.deletePanelSource(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['panel-sources'] })
    },
  })

  const setDefaultSourceMutation = useMutation({
    mutationFn: (id: string) => api.setDefaultPanelSource(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['panel-sources'] })
    },
  })

  const resetSourceForm = () => {
    setSourceFormData({
      name: '',
      url: '',
      username: '',
      password: '',
      category_filter: '',
      is_default: false,
    })
  }

  const openSourceModal = (source?: SportsPanelSource) => {
    if (source) {
      setEditingSource(source)
      setSourceFormData({
        name: source.name,
        url: source.url,
        username: source.username,
        password: '', // Don't pre-fill password
        category_filter: source.category_filter || '',
        is_default: source.is_default,
      })
    } else {
      setEditingSource(null)
      resetSourceForm()
    }
    setShowSourceModal(true)
  }

  const testSourceConnection = async (sourceId: string) => {
    setTestingSourceId(sourceId)
    try {
      const result = await api.testPanelSourceConnection(sourceId)
      setSourceTestResults({
        ...sourceTestResults,
        [sourceId]: { success: result.success, message: result.error || result.message }
      })
    } catch (error: any) {
      setSourceTestResults({
        ...sourceTestResults,
        [sourceId]: { success: false, message: error.message }
      })
    } finally {
      setTestingSourceId(null)
      refetchSources()
    }
  }

  const saveSource = () => {
    if (editingSource) {
      updateSourceMutation.mutate({
        id: editingSource.id,
        source: {
          name: sourceFormData.name,
          url: sourceFormData.url,
          username: sourceFormData.username,
          password: sourceFormData.password || undefined, // Only send if changed
          category_filter: sourceFormData.category_filter || undefined,
        },
      })
    } else {
      createSourceMutation.mutate(sourceFormData)
    }
  }

  // Test connections
  const testPanelConnection = async () => {
    setTestingPanel(true)
    setPanelTestResult(null)
    try {
      const result = await api.testSportsPanelConnection(
        settings.panel_url,
        settings.panel_username,
        settings.panel_password
      )
      setPanelTestResult({ success: result.success, message: result.error || result.message })
    } catch (error: any) {
      setPanelTestResult({ success: false, message: error.message })
    } finally {
      setTestingPanel(false)
    }
  }

  const testEmbyConnection = async () => {
    setTestingEmby(true)
    setEmbyTestResult(null)
    try {
      const result = await api.testSportsEmbyConnection(settings.emby_url, settings.emby_api_key)
      setEmbyTestResult({
        success: result.success,
        message: result.success ? `Connected to ${result.server_name} (v${result.version})` : result.error,
      })
    } catch (error: any) {
      setEmbyTestResult({ success: false, message: error.message })
    } finally {
      setTestingEmby(false)
    }
  }

  const saveSettings = () => {
    updateSettingsMutation.mutate(settings)
  }

  // Filter teams by search
  const filteredTeams = teams?.filter((team: SportsTeam) =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const tabs = [
    { id: 'status' as Tab, name: 'Status', icon: Trophy },
    { id: 'settings' as Tab, name: 'Settings', icon: Settings },
    { id: 'sources' as Tab, name: 'Panel Sources', icon: Database },
    { id: 'leagues' as Tab, name: 'Leagues', icon: Calendar },
    { id: 'teams' as Tab, name: 'Team Mapping', icon: Users },
    { id: 'stadium' as Tab, name: 'Stadium', icon: Tv },
    { id: 'channels' as Tab, name: 'Generic Channels', icon: Hash },
  ]

  const Toggle = ({ enabled, onChange, disabled }: { enabled: boolean; onChange: () => void; disabled?: boolean }) => (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
        enabled ? 'bg-primary' : 'bg-muted'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`${
          enabled ? 'translate-x-5' : 'translate-x-0'
        } inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out`}
      />
    </button>
  )

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground flex items-center gap-2 sm:gap-3">
          <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
          Sports EPG
        </h1>
        <button
          onClick={() => refreshMutation.mutate(undefined)}
          disabled={refreshMutation.isPending}
          className="btn-primary shrink-0"
        >
          <RefreshCw className={`w-4 h-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh All</span>
          <span className="sm:hidden">Refresh</span>
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        {/* Tabs - horizontal scroll on mobile, vertical sidebar on lg */}
        <div className="lg:w-64 flex-shrink-0">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0 lg:space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-3 text-sm lg:text-base font-medium rounded-lg transition-colors whitespace-nowrap lg:w-full ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                <tab.icon className="w-4 h-4 lg:w-5 lg:h-5" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 bg-card rounded-lg border border-border p-4 sm:p-6">
          {/* Status Tab */}
          {activeTab === 'status' && sportsStatus && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">Service Status</h2>

              {/* Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-lg p-4 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    {sportsStatus.running ? (
                      <Play className="w-5 h-5 text-green-500" />
                    ) : (
                      <Pause className="w-5 h-5 text-yellow-500" />
                    )}
                    <span className="font-medium">Service</span>
                  </div>
                  <p className={`text-2xl font-bold ${sportsStatus.running ? 'text-green-500' : 'text-yellow-500'}`}>
                    {sportsStatus.running ? 'Running' : 'Stopped'}
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-5 h-5 text-primary" />
                    <span className="font-medium">Active Games</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{sportsStatus.active_games}</p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="w-5 h-5 text-primary" />
                    <span className="font-medium">Mapped Channels</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{sportsStatus.total_mapped_channels}</p>
                </div>
              </div>

              {/* Connections */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`rounded-lg p-4 border ${sportsStatus.panel_connected ? 'border-green-500/30 bg-green-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
                  <div className="flex items-center gap-2">
                    <Server className="w-5 h-5" />
                    <span className="font-medium">Panel API</span>
                    {sportsStatus.panel_connected ? (
                      <Check className="w-4 h-4 text-green-500 ml-auto" />
                    ) : (
                      <X className="w-4 h-4 text-red-500 ml-auto" />
                    )}
                  </div>
                </div>

                <div className={`rounded-lg p-4 border ${sportsStatus.emby_connected ? 'border-green-500/30 bg-green-500/10' : 'border-yellow-500/30 bg-yellow-500/10'}`}>
                  <div className="flex items-center gap-2">
                    <Tv className="w-5 h-5" />
                    <span className="font-medium">Emby</span>
                    {sportsStatus.emby_connected ? (
                      <Check className="w-4 h-4 text-green-500 ml-auto" />
                    ) : (
                      <span className="text-sm text-muted-foreground ml-auto">Not configured</span>
                    )}
                  </div>
                </div>
              </div>

              {/* League Status */}
              <div>
                <h3 className="text-lg font-medium mb-4">League Status</h3>
                <div className="space-y-2">
                  {sportsStatus.league_statuses?.map((league: { code: string; name: string; enabled: boolean; mapped_teams: number; team_count: number; active_games: number; use_generic_channels?: boolean; channel_count?: number; mapped_channels?: number }) => (
                    <div
                      key={league.code}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full ${league.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <span className="font-medium">{league.name}</span>
                        <span className="text-sm text-muted-foreground">({league.code.toUpperCase()})</span>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        {league.use_generic_channels ? (
                          <span>{league.mapped_channels || 0}/{league.channel_count || 0} channels</span>
                        ) : (
                          <span>{league.mapped_teams}/{league.team_count} teams</span>
                        )}
                        {league.active_games > 0 && (
                          <span className="px-2 py-1 bg-red-500/20 text-red-500 rounded text-xs font-medium">
                            {league.active_games} LIVE
                          </span>
                        )}
                        <button
                          onClick={() => refreshMutation.mutate(league.code)}
                          className="p-1 hover:bg-muted rounded"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Errors */}
              {sportsStatus.recent_errors && sportsStatus.recent_errors.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    Recent Errors
                  </h3>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 max-h-48 overflow-y-auto">
                    {sportsStatus.recent_errors.map((error: string, i: number) => (
                      <p key={i} className="text-sm text-red-500 font-mono">{error}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">Sports EPG Settings</h2>

              {/* Panel API Settings */}
              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  Panel API Configuration
                </h3>
                <p className="text-sm text-muted-foreground">
                  Configure your IPTV panel API for stream URL resolution.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Panel URL</label>
                    <input
                      type="text"
                      value={settings.panel_url}
                      onChange={(e) => setSettings({ ...settings, panel_url: e.target.value })}
                      placeholder="http://your-panel.com:8080"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      value={settings.panel_username}
                      onChange={(e) => setSettings({ ...settings, panel_username: e.target.value })}
                      className="input-field"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      value={settings.panel_password}
                      onChange={(e) => setSettings({ ...settings, panel_password: e.target.value })}
                      className="input-field"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    onClick={testPanelConnection}
                    disabled={testingPanel}
                    className="btn-secondary"
                  >
                    {testingPanel ? 'Testing...' : 'Test Connection'}
                  </button>
                  {panelTestResult && (
                    <span className={`flex items-center gap-2 text-sm ${panelTestResult.success ? 'text-green-500' : 'text-red-500'}`}>
                      {panelTestResult.success ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      {panelTestResult.message}
                    </span>
                  )}
                </div>
              </div>

              {/* Emby Settings */}
              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium flex items-center gap-2">
                      <Tv className="w-5 h-5" />
                      Emby Integration
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Automatically refresh Emby guide after EPG updates.
                    </p>
                  </div>
                  <Toggle
                    enabled={settings.emby_enabled}
                    onChange={() => setSettings({ ...settings, emby_enabled: !settings.emby_enabled })}
                  />
                </div>

                {settings.emby_enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div>
                      <label className="form-label">Emby URL</label>
                      <input
                        type="text"
                        value={settings.emby_url}
                        onChange={(e) => setSettings({ ...settings, emby_url: e.target.value })}
                        placeholder="http://your-emby:8096"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="form-label">API Key</label>
                      <input
                        type="password"
                        value={settings.emby_api_key}
                        onChange={(e) => setSettings({ ...settings, emby_api_key: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <button
                        onClick={testEmbyConnection}
                        disabled={testingEmby}
                        className="btn-secondary"
                      >
                        {testingEmby ? 'Testing...' : 'Test Connection'}
                      </button>
                      {embyTestResult && (
                        <span className={`ml-4 text-sm ${embyTestResult.success ? 'text-green-500' : 'text-red-500'}`}>
                          {embyTestResult.message}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* EPG Output Settings */}
              <div className="p-5 bg-muted/50 rounded-lg border border-border space-y-4">
                <h3 className="text-lg font-medium">EPG Output</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Output Directory</label>
                    <input
                      type="text"
                      value={settings.epg_output_dir}
                      onChange={(e) => setSettings({ ...settings, epg_output_dir: e.target.value })}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="form-label">Timezone</label>
                    <select
                      value={settings.timezone}
                      onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                      className="input-field"
                    >
                      <option value="America/New_York">Eastern (ET)</option>
                      <option value="America/Chicago">Central (CT)</option>
                      <option value="America/Denver">Mountain (MT)</option>
                      <option value="America/Los_Angeles">Pacific (PT)</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={saveSettings}
                  disabled={updateSettingsMutation.isPending}
                  className="btn-primary"
                >
                  {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}

          {/* Sources Tab */}
          {activeTab === 'sources' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Panel Sources</h2>
                  <p className="text-muted-foreground mt-1">
                    Configure multiple IPTV panel sources for different leagues. Each league can use a specific source for stream resolution.
                  </p>
                </div>
                <button
                  onClick={() => openSourceModal()}
                  className="btn-primary"
                >
                  <Plus className="w-4 h-4" />
                  Add Source
                </button>
              </div>

              {/* Sources List */}
              <div className="space-y-4">
                {panelSources && panelSources.length > 0 ? (
                  panelSources.map((source: SportsPanelSource) => (
                    <div
                      key={source.id}
                      className={`p-5 rounded-lg border ${
                        source.is_default
                          ? 'border-primary/50 bg-primary/5'
                          : 'border-border bg-muted/50'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            source.is_default ? 'bg-primary/20' : 'bg-muted'
                          }`}>
                            <Database className={`w-6 h-6 ${source.is_default ? 'text-primary' : 'text-muted-foreground'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{source.name}</h3>
                              {source.is_default && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-primary/20 text-primary rounded">
                                  Default
                                </span>
                              )}
                              {!source.enabled && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-yellow-500/20 text-yellow-500 rounded">
                                  Disabled
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{source.url}</p>
                            {source.category_filter && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Category filter: {source.category_filter}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {/* Connection Status */}
                          <div className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm ${
                            source.last_error
                              ? 'bg-red-500/10 text-red-500'
                              : source.last_connected_at
                                ? 'bg-green-500/10 text-green-500'
                                : 'bg-muted text-muted-foreground'
                          }`}>
                            {source.last_error ? (
                              <>
                                <X className="w-4 h-4" />
                                <span className="hidden sm:inline">Error</span>
                              </>
                            ) : source.last_connected_at ? (
                              <>
                                <Check className="w-4 h-4" />
                                <span className="hidden sm:inline">{source.stream_count} streams</span>
                              </>
                            ) : (
                              <span>Not tested</span>
                            )}
                          </div>

                          {/* Test button */}
                          <button
                            onClick={() => testSourceConnection(source.id)}
                            disabled={testingSourceId === source.id}
                            className="btn-secondary text-sm"
                          >
                            {testingSourceId === source.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              'Test'
                            )}
                          </button>

                          {/* Set Default button */}
                          {!source.is_default && (
                            <button
                              onClick={() => setDefaultSourceMutation.mutate(source.id)}
                              disabled={setDefaultSourceMutation.isPending}
                              className="btn-secondary text-sm"
                              title="Set as default"
                            >
                              <Star className="w-4 h-4" />
                            </button>
                          )}

                          {/* Edit button */}
                          <button
                            onClick={() => openSourceModal(source)}
                            className="btn-secondary text-sm"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>

                          {/* Delete button */}
                          <button
                            onClick={() => {
                              if (confirm(`Delete source "${source.name}"? This cannot be undone.`)) {
                                deleteSourceMutation.mutate(source.id)
                              }
                            }}
                            disabled={deleteSourceMutation.isPending}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Error message */}
                      {source.last_error && (
                        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-500">
                          {source.last_error}
                        </div>
                      )}

                      {/* Test result */}
                      {sourceTestResults[source.id] && (
                        <div className={`mt-3 p-3 rounded text-sm ${
                          sourceTestResults[source.id].success
                            ? 'bg-green-500/10 border border-green-500/30 text-green-500'
                            : 'bg-red-500/10 border border-red-500/30 text-red-500'
                        }`}>
                          {sourceTestResults[source.id].success ? (
                            <span className="flex items-center gap-2">
                              <Check className="w-4 h-4" />
                              Connection successful
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <X className="w-4 h-4" />
                              {sourceTestResults[source.id].message}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted-foreground py-12 bg-muted/50 rounded-lg border border-border">
                    <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No panel sources configured</p>
                    <p className="text-sm mt-2">Add a panel source to enable stream resolution for sports EPG</p>
                    <button
                      onClick={() => openSourceModal()}
                      className="btn-primary mt-4"
                    >
                      <Plus className="w-4 h-4" />
                      Add First Source
                    </button>
                  </div>
                )}
              </div>

              {/* Source Modal */}
              {showSourceModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-card rounded-lg border border-border w-full max-w-md p-6 space-y-4">
                    <h3 className="text-lg font-semibold">
                      {editingSource ? 'Edit Panel Source' : 'Add Panel Source'}
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="form-label">Name</label>
                        <input
                          type="text"
                          value={sourceFormData.name}
                          onChange={(e) => setSourceFormData({ ...sourceFormData, name: e.target.value })}
                          placeholder="e.g., Main Provider, NCAAB Provider"
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="form-label">Panel URL</label>
                        <input
                          type="text"
                          value={sourceFormData.url}
                          onChange={(e) => setSourceFormData({ ...sourceFormData, url: e.target.value })}
                          placeholder="http://panel.example.com:8080"
                          className="input-field"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="form-label">Username</label>
                          <input
                            type="text"
                            value={sourceFormData.username}
                            onChange={(e) => setSourceFormData({ ...sourceFormData, username: e.target.value })}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="form-label">Password</label>
                          <input
                            type="password"
                            value={sourceFormData.password}
                            onChange={(e) => setSourceFormData({ ...sourceFormData, password: e.target.value })}
                            placeholder={editingSource ? '(unchanged)' : ''}
                            className="input-field"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="form-label">Category Filter (optional)</label>
                        <input
                          type="text"
                          value={sourceFormData.category_filter}
                          onChange={(e) => setSourceFormData({ ...sourceFormData, category_filter: e.target.value })}
                          placeholder="e.g., Sports, NCAAB"
                          className="input-field"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Only fetch streams from this category
                        </p>
                      </div>

                      {!editingSource && (
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={sourceFormData.is_default}
                            onChange={(e) => setSourceFormData({ ...sourceFormData, is_default: e.target.checked })}
                            className="w-4 h-4 rounded border-border"
                          />
                          <span className="text-sm">Set as default source</span>
                        </label>
                      )}
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-border">
                      <button
                        onClick={() => {
                          setShowSourceModal(false)
                          setEditingSource(null)
                          resetSourceForm()
                        }}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveSource}
                        disabled={createSourceMutation.isPending || updateSourceMutation.isPending ||
                          !sourceFormData.name || !sourceFormData.url || !sourceFormData.username ||
                          (!editingSource && !sourceFormData.password)}
                        className="btn-primary"
                      >
                        {createSourceMutation.isPending || updateSourceMutation.isPending
                          ? 'Saving...'
                          : editingSource ? 'Update' : 'Create'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Leagues Tab */}
          {activeTab === 'leagues' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">League Configuration</h2>
                <p className="text-muted-foreground mt-1">
                  Enable leagues and configure their channel mode. Team-based mode maps each team to a stream.
                  Generic channels mode uses numbered channels with game assignments from the panel API.
                </p>
              </div>

              <div className="space-y-4">
                {leagues?.map((league: SportsLeague) => (
                  <div
                    key={league.code}
                    className="p-5 bg-muted/50 rounded-lg border border-border"
                  >
                    {/* League Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <Toggle
                          enabled={league.enabled}
                          onChange={() => updateLeagueMutation.mutate({
                            code: league.code,
                            update: { enabled: !league.enabled }
                          })}
                        />
                        <div>
                          <p className="font-medium text-lg">{league.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {league.use_generic_channels
                              ? `${league.generic_channel_count || 0} generic channels`
                              : `${league.total_teams} teams | ${league.mapped_teams || 0} mapped`}
                          </p>
                        </div>
                      </div>

                      <div className="text-right text-sm text-muted-foreground">
                        {league.last_score_update && (
                          <p>Updated: {new Date(league.last_score_update).toLocaleTimeString()}</p>
                        )}
                        {league.live_games && league.live_games > 0 && (
                          <p className="text-red-500 font-medium">{league.live_games} games live</p>
                        )}
                      </div>
                    </div>

                    {/* League Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-border">
                      {/* Channel Mode Toggle */}
                      <div>
                        <label className="form-label text-sm">Channel Mode</label>
                        <div className="flex gap-1 bg-muted rounded-lg p-1">
                          <button
                            onClick={() => {
                              if (league.use_generic_channels) {
                                updateLeagueMutation.mutate({
                                  code: league.code,
                                  update: { use_generic_channels: false }
                                })
                              }
                            }}
                            className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
                              !league.use_generic_channels
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            Team-Based
                          </button>
                          <button
                            onClick={() => {
                              if (!league.use_generic_channels) {
                                updateLeagueMutation.mutate({
                                  code: league.code,
                                  update: {
                                    use_generic_channels: true,
                                    generic_channel_count: 8 // Default to 8 channels
                                  }
                                })
                              }
                            }}
                            className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
                              league.use_generic_channels
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            Generic Channels
                          </button>
                        </div>
                      </div>

                      {/* Panel Source */}
                      <div>
                        <label className="form-label text-sm">Panel Source</label>
                        <select
                          value={league.panel_source_id || ''}
                          onChange={(e) => updateLeagueMutation.mutate({
                            code: league.code,
                            update: { panel_source_id: e.target.value || null }
                          })}
                          className="input-field"
                        >
                          <option value="">Use Default</option>
                          {panelSources?.map((source: SportsPanelSource) => (
                            <option key={source.id} value={source.id}>
                              {source.name} {source.is_default && '(default)'}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Panel Category */}
                      <div>
                        <label className="form-label text-sm">Panel Category</label>
                        <input
                          type="text"
                          value={league.panel_category || ''}
                          onChange={(e) => updateLeagueMutation.mutate({
                            code: league.code,
                            update: { panel_category: e.target.value }
                          })}
                          placeholder="e.g., NFL"
                          className="input-field"
                        />
                      </div>

                      {/* Channel Count (only for generic mode) */}
                      {league.use_generic_channels && (
                        <div>
                          <label className="form-label text-sm">Channel Count</label>
                          <input
                            type="number"
                            min="1"
                            max="32"
                            value={league.generic_channel_count || 8}
                            onChange={(e) => {
                              const count = parseInt(e.target.value) || 8
                              updateLeagueMutation.mutate({
                                code: league.code,
                                update: { generic_channel_count: count }
                              })
                            }}
                            className="input-field"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Teams Tab */}
          {activeTab === 'teams' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Team Channel Mapping</h2>
                <p className="text-muted-foreground mt-1">
                  Map teams to existing streams for EPG generation. Only available for leagues using team-based mode.
                </p>
              </div>

              {/* League Selector - only show team-based leagues */}
              <div className="flex gap-4">
                <select
                  value={selectedLeague || ''}
                  onChange={(e) => setSelectedLeague(e.target.value || null)}
                  className="input-field"
                >
                  <option value="">Select a league...</option>
                  {leagues?.filter((l: SportsLeague) => l.enabled && !l.use_generic_channels).map((league: SportsLeague) => (
                    <option key={league.code} value={league.code}>
                      {league.name}
                    </option>
                  ))}
                </select>

                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search teams..."
                    className="input-field pl-10"
                  />
                </div>
              </div>

              {/* Team List */}
              {selectedLeague && filteredTeams && (
                <div className="space-y-2">
                  {filteredTeams.map((team: SportsTeam) => (
                    <div
                      key={team.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border"
                    >
                      <div className="flex items-center gap-3">
                        {team.logo_url && (
                          <img
                            src={team.logo_url}
                            alt={team.name}
                            className="w-8 h-8 object-contain"
                          />
                        )}
                        <div>
                          <p className="font-medium">
                            {team.city} {team.name}
                            {team.abbreviation && (
                              <span className="text-muted-foreground ml-2">({team.abbreviation})</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {team.conference} {team.division && `- ${team.division}`}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* Current mapping badge */}
                        {team.mapped_stream_name && (
                          <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">
                            {team.mapped_stream_name}
                          </span>
                        )}

                        {/* Stream mapping dropdown - searchable */}
                        <SearchableStreamSelect
                          streams={streams?.streams || []}
                          value={team.mapped_stream_id}
                          onChange={(streamId) => {
                            if (streamId) {
                              if (team.mapping_id) {
                                updateMappingMutation.mutate({
                                  mappingId: team.mapping_id,
                                  streamId: streamId,
                                })
                              } else {
                                createMappingMutation.mutate({
                                  teamId: team.id,
                                  streamId: streamId,
                                })
                              }
                            } else if (team.mapping_id) {
                              deleteMappingMutation.mutate(team.mapping_id)
                            }
                          }}
                          placeholder={team.mapped_stream_id ? 'Change stream...' : 'Select stream...'}
                          className="w-full sm:w-72"
                        />

                        {/* Delete mapping button */}
                        {team.mapping_id && (
                          <button
                            onClick={() => {
                              if (confirm(`Remove mapping for ${team.name}?`)) {
                                deleteMappingMutation.mutate(team.mapping_id!)
                              }
                            }}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded"
                            title="Remove mapping"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}

                        <Toggle
                          enabled={team.is_active}
                          onChange={() => api.updateSportsTeam(team.id, { is_active: !team.is_active })
                            .then(() => queryClient.invalidateQueries({ queryKey: ['sports-teams'] }))}
                        />
                      </div>
                    </div>
                  ))}

                  {filteredTeams.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No teams found</p>
                  )}
                </div>
              )}

              {!selectedLeague && (
                <div className="text-center text-muted-foreground py-12">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a league to view and map teams</p>
                </div>
              )}
            </div>
          )}

          {/* Stadium Tab */}
          {activeTab === 'stadium' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Stadium Sports</h2>
                <p className="text-muted-foreground mt-1">
                  Control which mapped OBA sports can appear in Stadium. Enabled sports with no playable games stay hidden automatically.
                </p>
              </div>
              <div className="space-y-3">
                {stadiumCategoriesLoading && (
                  <p className="text-muted-foreground">Loading Stadium categories...</p>
                )}
                {stadiumCategoriesError && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-red-400">
                    <AlertCircle className="h-5 w-5 shrink-0" />
                    <p className="flex-1">Stadium categories could not be loaded. The server returned an error.</p>
                    <button
                      type="button"
                      onClick={() => refetchStadiumCategories()}
                      className="rounded border border-red-500/40 px-3 py-1 text-sm hover:bg-red-500/10"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {updateStadiumMutation.isError && (
                  <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-red-400">
                    Stadium category changes could not be saved. Try again.
                  </div>
                )}
                {stadiumCategories?.map((category: StadiumCategoryConfig) => (
                  <div key={category.code} className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-lg border border-border bg-muted/30 p-4">
                    <div className="flex-1">
                      <div className="font-semibold text-foreground">{category.display_name}</div>
                      <div className="text-sm text-muted-foreground uppercase">{category.code}</div>
                    </div>
                    <label className="text-sm text-muted-foreground">
                      Order
                      <input
                        type="number"
                        defaultValue={category.display_order}
                        onBlur={(event) => updateStadiumMutation.mutate({ code: category.code, update: { display_order: Number(event.target.value) } })}
                        className="ml-2 w-20 rounded border border-border bg-background px-2 py-1 text-foreground"
                      />
                    </label>
                    <label className="text-sm text-muted-foreground">
                      Window
                      <select
                        value={category.availability_window_days}
                        onChange={(event) => updateStadiumMutation.mutate({ code: category.code, update: { availability_window_days: Number(event.target.value) } })}
                        className="ml-2 rounded border border-border bg-background px-2 py-1 text-foreground"
                      >
                        <option value={0}>Live only</option>
                        <option value={1}>1 day</option>
                        <option value={3}>3 days</option>
                        <option value={7}>7 days</option>
                        <option value={14}>14 days</option>
                      </select>
                    </label>
                    <Toggle
                      enabled={category.enabled}
                      onChange={() => updateStadiumMutation.mutate({ code: category.code, update: { enabled: !category.enabled } })}
                      disabled={updateStadiumMutation.isPending}
                    />
                  </div>
                ))}
                {!stadiumCategoriesLoading && !stadiumCategoriesError && stadiumCategories?.length === 0 && (
                  <p className="text-muted-foreground">No Stadium categories are configured.</p>
                )}
              </div>
            </div>
          )}

          {/* Generic Channels Tab */}
          {activeTab === 'channels' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Generic Channels</h2>
                <p className="text-muted-foreground mt-1">
                  Map numbered channels to streams. Game assignments are pulled from the panel API and enriched with ESPN data.
                </p>
              </div>

              {/* League Selector for generic channel leagues */}
              {genericChannelLeagues.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-4">
                    <select
                      value={selectedLeague || ''}
                      onChange={(e) => setSelectedLeague(e.target.value || null)}
                      className="input-field"
                    >
                      <option value="">Select a league...</option>
                      {genericChannelLeagues.map((league: SportsLeague) => (
                        <option key={league.code} value={league.code}>
                          {league.name} ({league.generic_channel_count} channels)
                        </option>
                      ))}
                    </select>

                    {selectedLeague && (
                      <button
                        onClick={() => refreshMutation.mutate(selectedLeague)}
                        disabled={refreshMutation.isPending}
                        className="btn-secondary"
                      >
                        <RefreshCw className={`w-4 h-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    )}
                  </div>

                  {/* Category Filter for Stream Selection */}
                  {selectedLeague && (
                    <div className="p-3 sm:p-4 bg-muted/30 rounded-lg border border-border">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className="flex items-center gap-2">
                          <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
                          <label className="text-sm font-medium whitespace-nowrap">Filter by category:</label>
                        </div>
                        {streamsLoading || categoriesLoading ? (
                          <span className="text-sm text-muted-foreground">Loading categories...</span>
                        ) : (
                          <select
                            value={selectedCategoryFilter}
                            onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                            className="input-field w-full sm:flex-1 sm:max-w-md"
                          >
                            <option value="all">All Categories ({streams?.streams?.length || 0} streams)</option>
                            <option value="none">Uncategorized</option>
                            {categories?.map((cat: { id: string; name: string }) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name} ({categoryStreamCounts[cat.id] || 0} streams)
                              </option>
                            ))}
                          </select>
                        )}
                        {selectedCategoryFilter !== 'all' && !streamsLoading && (
                          <span className="text-xs sm:text-sm text-muted-foreground">
                            {filteredStreams.length} shown
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Channel List */}
                  {selectedLeague && genericChannels && (
                    <div className="space-y-3">
                      {genericChannels.map((channel: SportsGenericChannel) => (
                        <div
                          key={channel.id}
                          className={`p-3 sm:p-4 rounded-lg border ${
                            channel.current_game
                              ? 'border-green-500/30 bg-green-500/5'
                              : 'border-border bg-muted/50'
                          }`}
                        >
                          {/* Mobile: Stack vertically, Desktop: Side by side */}
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            {/* Channel info */}
                            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                <span className="text-base sm:text-lg font-bold text-primary">
                                  {channel.channel_number}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium truncate">{channel.channel_name}</p>
                                {channel.current_game ? (
                                  <div className="text-xs sm:text-sm">
                                    <span className="text-green-500 font-medium">LIVE: </span>
                                    <span className="text-foreground">
                                      {channel.current_game.away_team_abbr} @ {channel.current_game.home_team_abbr}
                                    </span>
                                    {channel.current_game.game_status !== 'scheduled' && (
                                      <span className="ml-1 sm:ml-2 text-muted-foreground">
                                        ({channel.current_game.away_score} - {channel.current_game.home_score})
                                        <span className="hidden sm:inline">
                                          {channel.current_game.period && ` ${channel.current_game.period}`}
                                          {channel.current_game.clock && ` ${channel.current_game.clock}`}
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-xs sm:text-sm text-muted-foreground">No active game</p>
                                )}
                              </div>
                            </div>

                            {/* Controls - full width on mobile */}
                            <div className="flex items-center gap-2 sm:gap-4">
                              {/* Stream mapping - searchable dropdown */}
                              <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                                {channel.stream_id && (
                                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                )}
                                <SearchableStreamSelect
                                  streams={filteredStreams}
                                  value={channel.stream_id}
                                  onChange={(streamId) => {
                                    mapGenericChannelMutation.mutate({
                                      channelId: channel.id,
                                      streamId: streamId,
                                    })
                                  }}
                                  placeholder="Search streams..."
                                  className="w-full sm:w-72"
                                />
                              </div>

                              <Toggle
                                enabled={channel.enabled}
                                onChange={() => updateGenericChannelMutation.mutate({
                                  channelId: channel.id,
                                  enabled: !channel.enabled,
                                })}
                              />
                            </div>
                          </div>

                          {/* Game details */}
                          {channel.current_game && (
                            <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Status: </span>
                                <span className={channel.current_game.game_status === 'in_progress' ? 'text-green-500' : ''}>
                                  {channel.current_game.game_status}
                                </span>
                              </div>
                              {channel.current_game.venue && (
                                <div>
                                  <span className="text-muted-foreground">Venue: </span>
                                  {channel.current_game.venue}
                                </div>
                              )}
                              {channel.current_game.down_distance && (
                                <div>
                                  <span className="text-muted-foreground">Down: </span>
                                  {channel.current_game.down_distance}
                                </div>
                              )}
                              {channel.current_game.is_red_zone && (
                                <div className="text-red-500 font-medium">
                                  RED ZONE
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!selectedLeague && (
                    <div className="text-center text-muted-foreground py-12">
                      <Hash className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a league to view and map channels</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-muted-foreground py-12">
                  <Hash className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No leagues configured for generic channels</p>
                  <p className="text-sm mt-2">Enable a league with "Generic Channels" mode in the Leagues tab</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
